import { useEffect, useState } from "react";
import { BookOpen, Check, Copy, FileDown, FileJson } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


const METHOD_STYLE = {
    GET: "border-emerald-500/30 bg-emerald-950/40 text-emerald-300",
    POST: "border-cyan-500/30 bg-cyan-950/40 text-cyan-300",
    PATCH: "border-amber-500/30 bg-amber-950/40 text-amber-300",
    DELETE: "border-rose-500/30 bg-rose-950/40 text-rose-300",
    WS: "border-violet-500/30 bg-violet-950/40 text-violet-300",
};

const AUTH_STYLE = {
    public: "text-emerald-400",
    session: "text-slate-400",
};

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // fallback for browsers/contexts where the clipboard API is blocked
        try {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            const ok = document.execCommand("copy");
            ta.remove();
            return ok;
        } catch {
            return false;
        }
    }
};

const CopyBtn = ({ text, testid }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            data-testid={testid}
            onClick={async () => {
                if (await copyText(text)) {
                    setCopied(true);
                    toast.success("Copied");
                    setTimeout(() => setCopied(false), 1500);
                } else {
                    toast.error("Copy failed — select and copy manually");
                }
            }}
            className="shrink-0 rounded border border-slate-700 p-1.5 text-slate-500 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
            aria-label="Copy curl"
        >
            {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
        </button>
    );
};

export default function ApiReference({ appId }) {
    const [groups, setGroups] = useState(null);
    const freshKey = sessionStorage.getItem(`mdh-new-key-${appId}`);

    useEffect(() => {
        setGroups(null);
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/reference`, { credentials: "include" });
                if (r.ok) setGroups((await r.json()).groups || []);
            } catch { /* non-fatal */ }
        })();
    }, [appId]);

    if (!groups) return null;

    return (
        <div data-testid="api-reference" className="glass-panel rounded-xl p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                <BookOpen className="h-4 w-4 text-cyan-400" />
                API reference — what's live for this app
            </h3>
            <p className="mt-1.5 font-mono text-[10px] text-slate-500">
                The functional endpoints for this exact app — not the spec. Auth:{" "}
                <span className="text-emerald-400">public</span> ·{" "}
                <span className="text-cyan-300">api key</span> ·{" "}
                <span className="text-slate-400">session</span>
            </p>
            <div className="mt-3 flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-600">export:</span>
                <a
                    data-testid="api-ref-export-markdown"
                    href={`${API}/apps/${appId}/reference?format=markdown`}
                    className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                >
                    <FileDown className="h-3 w-3" /> markdown
                </a>
                <a
                    data-testid="api-ref-export-postman"
                    href={`${API}/apps/${appId}/reference?format=postman`}
                    className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                >
                    <FileJson className="h-3 w-3" /> postman
                </a>
            </div>
            <div className="mt-4 space-y-5">
                {groups.map((g, gi) => (
                    <div key={g.title} data-testid={`api-ref-group-${gi}`}>
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            {g.title}
                        </p>
                        <div className="space-y-1.5">
                            {g.endpoints.map((e, ei) => (
                                <div
                                    key={`${e.method}-${e.path}`}
                                    className="flex items-start gap-2.5 rounded-lg border border-slate-800/60 bg-slate-950/50 px-3 py-2"
                                >
                                    <span
                                        className={`mt-px w-14 shrink-0 rounded border px-1.5 py-0.5 text-center font-mono text-[9px] font-bold uppercase tracking-wider ${METHOD_STYLE[e.method] || METHOD_STYLE.GET}`}
                                    >
                                        {e.method}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-mono text-[11px] text-slate-200">
                                            {e.path}
                                        </p>
                                        <p className="font-mono text-[9.5px] leading-relaxed text-slate-500">
                                            <span className={AUTH_STYLE[e.auth.split(" ")[0]] || "text-cyan-300"}>
                                                {e.auth}
                                            </span>
                                            {" — "}
                                            {e.note}
                                        </p>
                                    </div>
                                    {e.curl && (
                                        <CopyBtn
                                            testid={`api-ref-copy-${gi}-${ei}`}
                                            text={e.curl.replaceAll("{BASE}", BACKEND).replaceAll("$KEY", freshKey || "mdk_live_…")}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
